import { v4 as uuid } from 'uuid';
import { registerPattern } from './adapter.ts';
import { saveDatabase, type MockDatabase, type MockRecord } from './database.ts';
import { ACCESS_PERMISSIONS, normalizeAccessPermissions } from './contracts/accessModel.ts';
import type { AccessContext, AccessPermissionKey } from './contracts/accessModel.ts';
import { effectivePermissions, evaluateAccess, explainAccess } from './contracts/accessEvaluator.ts';
import { currentUser, publicIdentity } from './identity.ts';
import { db, find, update } from './helpers.ts';
import { administrationScope, assertRecordScope, assertUserWriteScope, inAdministrationScope, normalized, resolvePsl, scopedAccessProfile, type AdministrationScope } from './adminPolicy.ts';
import { accessProfileFor } from './accessProfile.ts';

const GLOBAL_PSL_ID = '00000003-0000-4000-8000-000000000005';
const migrationId = 'normalized-access-model-3';
const roleNames: Record<string, string> = {
  admin: 'Administrator', superadmin: 'Administrator', qcdocadmin: 'Administrator', administrator: 'Administrator',
  cm: 'Category Manager', 'category manager': 'Category Manager', gql: 'Global Quality Lead', 'global quality lead': 'Global Quality Lead',
  qalab: 'QA Lab', 'qa lab': 'QA Lab', qalabsupervisor: 'QA Lab Supervisor', 'qa lab supervisor': 'QA Lab Supervisor',
  qatech: 'Quality Documenter', 'quality documenter': 'Quality Documenter', readonly: 'QA View', 'qa view': 'QA View',
  coaadmin: 'COA Administrator', reportviewer: 'Report Viewer', packagingadmin: 'Packaging Administrator',
  purchasingagent: 'Purchasing Agent', 'purchasing agent': 'Purchasing Agent', manager: 'Manager', groupleader: 'Group Leader',
  'group leader': 'Group Leader', logistics: 'Logistics', supplychain: 'Supply Chain', 'supply chain': 'Supply Chain',
  regulatory: 'Regulatory', formulaeditor: 'Formula Editor', 'formula editor': 'Formula Editor', hdfx: 'Hdfx',
  productowner: 'PSD', psd: 'PSD', productownerrole: 'PSD',
};
const roleGrants: Record<string, string[]> = {
  'Administrator': ACCESS_PERMISSIONS.map((permission) => permission.permissionKey),
  'Category Manager': ['Products.View', 'VQCA.View', 'VQCA.Edit', 'VQCA.SignCm', 'VQCARequests.View', 'VQCARequests.Create', 'VQCARequests.Edit', 'VQCARequests.Approve', 'COA.View', 'COA.Review', 'Samples.View', 'Experiments.View', 'Reports.View', 'Vendors.View', 'Vendors.Manage'],
  'Global Quality Lead': ['Products.View', 'Products.Approve', 'VQCA.View', 'VQCA.Create', 'VQCA.Edit', 'VQCA.SignGql', 'VQCARequests.View', 'VQCARequests.Approve', 'VQCARequests.Delete', 'COA.View', 'COA.Review', 'COA.Delete', 'Reports.View', 'Reports.Export', 'Vendors.View', 'Vendors.Manage'],
  'QA Lab': ['VQCA.View', 'COA.View', 'COA.Create', 'COA.Review', 'Samples.View', 'Samples.Create', 'Samples.Edit', 'Experiments.View', 'Experiments.Create', 'Experiments.Edit', 'QAReports.View', 'QAReports.Create', 'COQ.View', 'Reports.View'],
  'QA Lab Supervisor': ['VQCA.View', 'COA.View', 'COA.Review', 'Samples.View', 'Samples.Create', 'Samples.Edit', 'Samples.Approve', 'Experiments.View', 'Experiments.Edit', 'Experiments.Approve', 'QAReports.View', 'QAReports.Create', 'QAReports.Approve', 'QAReports.ApproveTestCertificate', 'COQ.View', 'Reports.View', 'Vendors.View', 'Vendors.Manage'],
  'Quality Documenter': ['Products.View', 'VQCA.View', 'COA.View', 'COA.Create', 'COA.Edit', 'Samples.View', 'Samples.Create', 'Samples.Edit', 'Experiments.View', 'Experiments.Create', 'Experiments.Edit', 'QAReports.View', 'QAReports.Create', 'Reports.View'],
  'QA View': ['Products.View', 'VQCA.View', 'VQCARequests.View', 'COA.View', 'Samples.View', 'Experiments.View', 'COQ.View', 'Packaging.View', 'Reports.View', 'Reports.Export', 'Hdfx.View', 'Vendors.View', 'Alerts.View', 'CategoryLevels.View', 'ReferenceData.View'],
  'Purchasing Agent': ['VQCA.View', 'VQCARequests.View', 'VQCARequests.Create', 'COA.View', 'Reports.View'],
  'Manager': ['Products.View', 'VQCA.View', 'COA.View', 'Reports.View'],
  'Group Leader': ['Products.View', 'VQCA.View', 'COA.View', 'Reports.View'],
  'Logistics': ['COQ.View', 'COQ.Generate', 'Reports.View', 'Reports.Export'],
  'Supply Chain': ['VQCA.View', 'COA.View', 'COQ.View', 'Reports.View', 'Reports.Export'],
  'Regulatory': ['Products.View', 'VQCA.View', 'COA.View', 'Reports.View', 'Reports.Export'],
  'Formula Editor': ['Products.View', 'Products.Edit'],
  'Hdfx': ['Hdfx.View', 'Products.View'],
  'PSD': ['Products.View', 'Products.Create', 'Products.Edit', 'Products.Approve', 'STP.View', 'STP.Edit', 'Reports.View'],
  'COA Administrator': ['COA.View', 'COA.Create', 'COA.Edit', 'COA.Review', 'COA.Delete', 'COA.Restore', 'COA.Export'],
  'Report Viewer': ['Reports.View', 'Reports.Export'],
  'Packaging Administrator': ['Packaging.View', 'Packaging.Create', 'Packaging.Edit', 'Packaging.ManageAssignments'],
};
const splitScopedRole = (value: string) => value.trim().match(/^(.+?)\s+-\s+(.+)$/);
const canonicalRoleName = (value: string) => {
  const raw = value.trim();
  const scoped = splitScopedRole(raw);
  if (!scoped) return roleNames[raw.toLowerCase()] ?? raw;
  const [, base, scope] = scoped;
  return `${roleNames[base.trim().toLowerCase()] ?? base.trim()} - ${scope.trim()}`;
};
const baseName = (value: string) => {
  const raw = canonicalRoleName(value);
  const name = splitScopedRole(raw)?.[1].trim() ?? raw;
  return roleNames[name.toLowerCase()] ?? name;
};

export function predefinedRolePermissions(roleName: string): string[] | undefined {
  const name = roleName.replace(/\s+\[[^\]]+\](?:\s+\(\d+\))?$/, '');
  const permissions = roleGrants[baseName(name)];
  return permissions ? [...permissions] : undefined;
}
const active = (row: MockRecord) => row.isActive !== false && row.enabled !== false;
const actor = () => currentUser();
const requireGlobalAdmin = (permission: AccessPermissionKey = 'Administration.Roles.Manage') => {
  const scope = administrationScope(actor(), permission);
  if (!scope.global) throw new Error('Only a Global administrator can manage role definitions and permissions.');
  return scope;
};
const permissionRows = () => db().accessPermissions = normalizeAccessPermissions(db().accessPermissions
  ?? ACCESS_PERMISSIONS.map((permission) => ({ oid: permission.permissionKey, ...permission })));
const accessContext = (userId: string): AccessContext => accessProfileFor(db().users.find((user) => String(user.userId ?? user.oid) === userId) ?? { oid: userId, userId });

function roleViewingScope(user: MockRecord): AdministrationScope {
  const scopes = ['Administration.Roles.View', 'Administration.Roles.Manage'].map((permission) => administrationScope(user, permission as AccessPermissionKey));
  return { global: scopes.some((scope) => scope.global), pslIds: new Set(scopes.flatMap((scope) => [...scope.pslIds])) };
}

export function canAccessResourcePermission(userId: string, permissionKey: AccessPermissionKey, pslValue: unknown): boolean {
  const permission = permissionRows().find((row) => row.permissionKey === permissionKey);
  const psl = resolvePsl(pslValue);
  if (permission?.scopeRule === 'record' && !psl) return false;
  return evaluateAccess(accessContext(userId), permissionKey, psl?.oid ?? null).allowed;
}

export function requireResourcePermission(permissionKey: AccessPermissionKey, pslValue: unknown): void {
  const user = currentUser();
  if (canAccessResourcePermission(String(user.userId ?? user.oid), permissionKey, pslValue)) return;
  const psl = resolvePsl(pslValue);
  const label = psl?.name ?? 'this record';
  throw new Error(`You do not have ${permissionKey} permission in ${label}.`);
}

function validatePermissionGrants(input: unknown, roleId?: string) {
  if (!Array.isArray(input)) throw new Error('Permission grants must be an array.');
  const permissions = new Map(permissionRows().map((permission) => [String(permission.permissionKey), permission]));
  const grants = new Map<string, { roleId: string; permissionId: string; pslId: string }>();
  const submitted = new Set<string>();
  const existing = new Set((db().rolePermissionGrants ?? []).filter((grant) => grant.roleId === roleId)
    .map((grant) => `${grant.permissionId}:${grant.pslId}`));
  for (const item of input) {
    if (!item || typeof item !== 'object') throw new Error('Each permission grant requires a permission and PSL.');
    if (Object.keys(item).some((key) => key !== 'permissionId' && key !== 'pslId')) throw new Error('Permission grants accept permissionId and pslId only.');
    const permissionId = String(item.permissionId ?? '');
    const permission = permissions.get(permissionId);
    const psl = db().psls.find((entry) => entry.oid === String(item.pslId));
    if (!permission) throw new Error('A selected permission is not in the system catalogue.');
    if (!psl) throw new Error('Choose active PSLs only.');
    const submittedKey = `${permissionId}:${psl.oid}`;
    if (submitted.has(submittedKey)) throw new Error('Permission grants must be unique for each permission and PSL.');
    submitted.add(submittedKey);
    if (!active(psl)) {
      if (!existing.has(submittedKey)) throw new Error('Choose active PSLs only.');
      grants.set(submittedKey, { roleId: '', permissionId, pslId: String(psl.oid) });
      continue;
    }
    grants.set(submittedKey, { roleId: '', permissionId, pslId: String(psl.oid) });
  }
  return [...grants.values()];
}

function addAudit(type: string, subject: string, before: unknown, after: unknown) {
  const user = currentUser();
  (db().accessAudit ??= []).push({ oid: uuid(), eventId: uuid(), eventType: type, subject,
    actorUserId: String(user.userId ?? user.oid), actorName: String(user.userName), before: before ?? null,
    after: after ?? null, occurredOn: new Date().toISOString() });
  saveDatabase();
}

function roleResponse(role: MockRecord): MockRecord & { roleName: string; permissionCount: number; permissionGrants: { permissionId: string; pslId: string }[]; assignmentCount: number; assignedPslCount: number } {
  const { roleType: _roleType, enabled: _enabled, version: _version, ...roleRecord } = role;
  const permissionGrants = (db().rolePermissionGrants ?? []).filter((row) => row.roleId === role.oid);
  const memberships = (db().userRoleMemberships ?? []).filter((row) => row.roleId === role.oid);
  return { ...publicIdentity(roleRecord), roleName: String(role.roleName), isActive: role.isActive !== false, permissionCount: new Set(permissionGrants.map((row) => row.permissionId)).size,
    permissionGrants: permissionGrants.map((grant) => ({ permissionId: String(grant.permissionId), pslId: String(grant.pslId) })),
    assignmentCount: new Set(memberships.map((row) => String(row.userId))).size,
    assignedPslCount: new Set(permissionGrants.map((grant) => String(grant.pslId))).size };
}

function visibleRoleResponse(role: MockRecord, scope: AdministrationScope,
  assignmentScope = administrationScope(actor(), 'Administration.Assignments.Manage')): ReturnType<typeof roleResponse> {
  const response = roleResponse(role);
  const canAssign = assignmentScope.global || assignmentScope.pslIds.size > 0
    && response.permissionGrants.every((grant) => assignmentScope.pslIds.has(grant.pslId));
  if (scope.global) return { ...response, canAssign };
  const grants = response.permissionGrants.filter((grant) => scope.pslIds.has(grant.pslId));
  return { ...response, canAssign, permissionGrants: grants, permissionCount: new Set(grants.map((grant) => grant.permissionId)).size,
    assignedPslCount: new Set(grants.map((grant) => grant.pslId)).size,
    assignmentCount: (db().userRoleMemberships ?? []).filter((membership) => membership.roleId === role.oid
      && db().users.some((user) => user.oid === membership.userId && inAdministrationScope(scope, user))).length };
}

export function ensureAccessModel(database: MockDatabase): boolean {
  database.rolePslPermissions ??= [];
  const staleDefinition = database.accessPermissions?.some((permission) => permission.permissionKey === 'Administration.Roles.Manage'
    && permission.scopeRule !== 'any-psl') ?? false;
  if (database.accessPermissions) database.accessPermissions = normalizeAccessPermissions(database.accessPermissions);
  if (database.accessMigrations?.some((row) => row.oid === migrationId)) return staleDefinition;
  const globalPsl = database.psls.find((psl) => normalized(psl.name) === 'global') ?? {
    oid: GLOBAL_PSL_ID, name: 'Global', enabled: true, isActive: true, isGlobal: true,
  };
  globalPsl.isGlobal = true;
  if (!database.psls.some((psl) => psl.oid === globalPsl.oid)) database.psls.push(globalPsl);
  database.accessPermissions = ACCESS_PERMISSIONS.map((permission) => ({ oid: permission.permissionKey, ...permission }));
  database.accessRoles ??= [];
  database.rolePermissions ??= [];
  database.userRoleAssignments ??= [];
  database.accessAudit ??= [];
  const roleIdByName = new Map<string, string>();
  for (const user of database.users ?? []) {
    if (user.portal === 'QCDOC-VENDOR') continue;
    const source = Array.isArray(user.roleAssignments) ? user.roleAssignments : Array.isArray(user.roles) ? user.roles : [];
    for (const item of source) {
      const value = typeof item === 'string' ? item : String((item as Record<string, unknown>).roleName ?? (item as Record<string, unknown>).roleId ?? '');
      const name = canonicalRoleName(value);
      if (!name || roleIdByName.has(normalized(name))) continue;
      let role = database.accessRoles.find((entry) => normalized(entry.roleName) === normalized(name));
      if (!role) {
        const definition = database.roles.find((entry) => normalized(entry.roleName) === normalized(value) || normalized(entry.roleName) === normalized(name));
        const definitionId = String(definition?.roleId ?? uuid());
          role = { oid: definitionId, roleId: definitionId, roleName: name,
            description: `${name} access`, isActive: true };
        database.accessRoles.push(role);
      }
      roleIdByName.set(normalized(name), String(role.oid));
      const grants = roleGrants[baseName(name)] ?? [];
      if (baseName(name) === 'Administrator') {
        applySeedGrants(database, String(role.oid), ACCESS_PERMISSIONS.map((permission) => permission.permissionKey));
      } else applySeedGrants(database, String(role.oid), grants);
    }
  }
  for (const oldRole of database.roles ?? []) {
    if (oldRole.portal === 'QCDOC-VENDOR') continue;
    const name = canonicalRoleName(String(oldRole.roleName ?? ''));
    if (!name || roleIdByName.has(normalized(name))) continue;
      const role = { oid: uuid(), roleId: uuid(), roleName: name, description: `${name} access`, isActive: true };
    database.accessRoles.push(role);
    roleIdByName.set(normalized(name), String(role.oid));
    applySeedGrants(database, String(role.oid), roleGrants[baseName(name)] ?? []);
  }
  for (const user of database.users ?? []) {
    if (user.portal === 'QCDOC-VENDOR') continue;
    const source = Array.isArray(user.roleAssignments) ? user.roleAssignments : Array.isArray(user.roles) ? user.roles : [];
    for (const item of source) {
      const raw = typeof item === 'string' ? item : String((item as Record<string, unknown>).roleName ?? (item as Record<string, unknown>).roleId ?? '');
      const split = raw.match(/^(.+?)\s+-\s+(.+)$/);
      const roleName = canonicalRoleName(raw);
      const roleId = roleIdByName.get(normalized(roleName));
      if (!roleId || normalized(raw) === 'external user') continue;
      const pslName = split?.[2] ?? (baseName(roleName) === 'Administrator' ? 'Global' : String(user.pslName ?? user.psl ?? 'Global'));
      const canonicalPslName = normalized(pslName) === 'pe' ? 'production enhancement' : normalized(pslName);
      const psl = database.psls.find((entry) => normalized(entry.name) === canonicalPslName) ?? globalPsl;
      const userId = String(user.userId ?? user.oid);
      if (!database.userRoleAssignments.some((entry) => entry.userId === userId && entry.roleId === roleId && entry.pslId === psl.oid)) {
        database.userRoleAssignments.push({ oid: uuid(), userId, roleId, pslId: String(psl.oid), assignedBy: 'legacy migration', assignedOn: new Date().toISOString() });
      }
    }
  }
  (database.accessMigrations ??= []).push({ oid: migrationId, appliedAt: new Date().toISOString() });
  return true;
}

function applySeedGrants(database: MockDatabase, roleId: string, keys: string[]) {
  const grants = database.rolePermissions ??= [];
  const current = new Set(grants.filter((row) => row.roleId === roleId).map((row) => row.permissionKey));
  for (const permissionKey of keys) {
    if (!current.has(permissionKey)) grants.push({ oid: uuid(), roleId, permissionKey });
  }
}

export function validateAssignmentChoices(user: MockRecord, input: unknown, userScope?: AdministrationScope) {
  if (!Array.isArray(input)) throw new Error('Role IDs must be an array of distinct active role IDs.');
  const scope = administrationScope(user, 'Administration.Assignments.Manage');
  if (input.length && !scope.global && !scope.pslIds.size) throw new Error('Administration.Assignments.Manage permission is required.');
  const seen = new Set<string>();
  return input.map((roleId: unknown) => {
    if (typeof roleId !== 'string' || seen.has(roleId)) throw new Error('Choose distinct role IDs only.');
    seen.add(roleId);
    const role = find('accessRoles', roleId);
    if (role.isActive === false || role.portal === 'QCDOC-VENDOR') throw new Error('Inactive or vendor roles cannot be assigned internally.');
    const grants = (db().rolePermissionGrants ?? []).filter((grant) => grant.roleId === role.oid);
    for (const grant of grants) {
      const psl = db().psls.find((entry) => entry.oid === grant.pslId);
      if (!psl || !active(psl)) throw new Error('Role has grants for an inactive PSL.');
      if (!scope.global && !scope.pslIds.has(psl.oid)) throw new Error('Cannot assign roles outside your assignment permission scope.');
      if (userScope && !userScope.global && !userScope.pslIds.has(psl.oid)) throw new Error('Cannot assign permissions outside the target user management scope.');
    }
    return { roleId: String(role.oid) };
  });
}

export function refreshAssignmentLabels(user: MockRecord): MockRecord {
  const userId = String(user.userId ?? user.oid);
  const compatibility = (db().userRoleMemberships ?? []).filter((join) => join.userId === userId).map((join) => {
    const role = db().accessRoles.find((entry) => entry.oid === join.roleId);
    return { roleId: join.roleId, roleName: String(role?.roleName ?? '') };
  });
  const refreshed = update('users', user.oid, { permissionGrantVersion: 4, roleAssignments: compatibility, roles: compatibility.map((join) => join.roleName) });
  delete refreshed.roleScopeVersion;
  if (compatibility.some((membership) => (db().rolePermissionGrants ?? []).some((grant) => grant.roleId === membership.roleId))) {
    delete refreshed.pendingAssignmentPslIds;
  }
  return refreshed;
}

export function registerAccessControl(): void {
  registerPattern('GET', /^\/api\/Access\/(permissions|roles|psls)$/i, (request) => {
    const user = actor();
    const permissions: AccessPermissionKey[] = ['Administration.Roles.View', 'Administration.Roles.Manage', 'Administration.Users.View', 'Administration.Users.Manage', 'Administration.Assignments.Manage'];
    if (!permissions.some((permission) => {
      const scope = administrationScope(user, permission);
      return scope.global || scope.pslIds.size > 0;
    })) throw new Error('This account does not have permission to view access settings.');
    const scopes = permissions.map((permission) => administrationScope(user, permission));
    const scope: AdministrationScope = { global: scopes.some((entry) => entry.global), pslIds: new Set(scopes.flatMap((entry) => [...entry.pslIds])) };
    if (request.path.toLowerCase().endsWith('/psls')) return db().psls.filter((psl) => active(psl) && (scope.global || scope.pslIds.has(psl.oid)));
    if (request.path.toLowerCase().endsWith('/permissions')) return permissionRows();
    return (db().accessRoles ?? []).filter((role) => scope.global || (db().rolePermissionGrants ?? [])
      .some((grant) => grant.roleId === role.oid && scope.pslIds.has(String(grant.pslId))))
      .map((role) => visibleRoleResponse(role, scope)).sort((left, right) => String(left.roleName).localeCompare(String(right.roleName)));
  });
  registerPattern('POST', /^\/api\/Access\/roles$/i, (request) => {
    requireGlobalAdmin();
    const input = request.body;
    if (Object.keys(input).some((key) => !['roleName', 'description'].includes(key))) throw new Error('Role creation accepts roleName and description only.');
    const roleName = String(input.roleName ?? '').trim();
    if (!roleName) throw new Error('A role name is required.');
    if ((db().accessRoles ?? []).some((role) => normalized(role.roleName) === normalized(roleName))) throw new Error('Role name already exists.');
    const role = { oid: uuid(), roleId: '', roleName, description: String(input.description ?? '').trim(), isActive: true };
    role.roleId = role.oid;
    db().accessRoles.push(role);
    db().rolePermissionGrants ??= [];
    addAudit('role.created', roleName, null, roleResponse(role));
    return roleResponse(role);
  });
  registerPattern('GET', /^\/api\/Access\/roles\/[^/]+$/i, (request) => {
    const scope = roleViewingScope(actor());
    if (!scope.global && !scope.pslIds.size) throw new Error('This account does not have permission to view roles.');
    const role = find('accessRoles', request.path.split('/').at(-1));
    if (!scope.global && !(db().rolePermissionGrants ?? []).some((grant) => grant.roleId === role.oid && scope.pslIds.has(String(grant.pslId)))) {
      throw new Error('This role is outside your administrator PSL scope.');
    }
    return visibleRoleResponse(role, scope);
  });
  registerPattern('PUT', /^\/api\/Access\/roles\/[^/]+$/i, (request) => {
    const user = actor();
    const scope = administrationScope(user, 'Administration.Roles.Manage');
    if (!scope.global && !scope.pslIds.size) throw new Error('Administration.Roles.Manage permission with Global or delegated PSL authority is required.');
    const viewingScope = roleViewingScope(user);
    const assignmentScope = administrationScope(user, 'Administration.Assignments.Manage');
    const role = find('accessRoles', request.path.split('/').at(-1));
    const input = request.body;
    if (Object.keys(input).some((key) => !['roleId', 'roleName', 'description', 'isActive', 'permissionGrants'].includes(key))) {
      throw new Error('Role updates accept permissionGrants, roleName, description and isActive only.');
    }
    if (input.roleId !== undefined && input.roleId !== role.oid) throw new Error('Role ID must match the target role.');
    if (!scope.global && (Object.keys(input).some((key) => key !== 'roleId' && key !== 'permissionGrants') || input.permissionGrants === undefined)) {
      throw new Error('Shared role metadata requires Global authority; delegated updates accept permissionGrants only.');
    }
    const before = roleResponse(role);
    const roleName = String(input.roleName ?? role.roleName).trim();
    if (!roleName) throw new Error('A role name is required.');
    if ((db().accessRoles ?? []).some((entry) => entry.oid !== role.oid && normalized(entry.roleName) === normalized(roleName))) throw new Error('Role name already exists.');
    const grants = input.permissionGrants === undefined ? undefined : validatePermissionGrants(input.permissionGrants, String(role.oid));
    const existing = (db().rolePermissionGrants ?? []).filter((grant) => grant.roleId === role.oid);
    const globalOnly = new Set(permissionRows().filter((permission) => permission.scopeRule === 'global-only').map((permission) => String(permission.permissionKey)));
    const editable = (grant: Record<string, unknown>) => scope.pslIds.has(String(grant.pslId)) && !globalOnly.has(String(grant.permissionId));
    if (!scope.global) {
      if (!existing.some(editable)) throw new Error('This role is outside your editable PSL scope.');
      for (const grant of grants ?? []) {
        const psl = db().psls.find((entry) => entry.oid === grant.pslId)!;
        if (!scope.pslIds.has(grant.pslId) || !active(psl) || psl.isGlobal === true || normalized(psl.name) === 'global') {
          throw new Error('Permission grant targets must be active business PSLs within your editable scope.');
        }
        if (globalOnly.has(grant.permissionId) && !existing.some((row) => row.permissionId === grant.permissionId && row.pslId === grant.pslId)) {
          throw new Error('Global-only permission grants require Global authority.');
        }
      }
      if (existing.some((row) => scope.pslIds.has(String(row.pslId)) && globalOnly.has(String(row.permissionId))
        && !grants?.some((grant) => grant.permissionId === row.permissionId && grant.pslId === row.pslId))) {
        throw new Error('Global-only permission grants must remain unchanged.');
      }
    }
    if (scope.global) Object.assign(role, { roleName, description: String(input.description ?? role.description ?? '').trim(),
      isActive: input.isActive === undefined ? role.isActive !== false : Boolean(input.isActive) });
    if (grants !== undefined) {
      db().rolePermissionGrants = (db().rolePermissionGrants ?? []).filter((grant) => grant.roleId !== role.oid || !scope.global && !editable(grant));
      for (const grant of grants.filter((grant) => scope.global || editable(grant))) db().rolePermissionGrants.push({ oid: uuid(), ...grant, roleId: role.oid });
    }
    addAudit('role.updated', roleName, before, roleResponse(role));
    return visibleRoleResponse(role, viewingScope, assignmentScope);
  });
  registerPattern('GET', /^\/api\/Access\/users\/[^/]+\/assignments$/i, (request) => {
    const scope = administrationScope(actor(), 'Administration.Users.View');
    const user = find('users', request.path.split('/')[4]);
    assertRecordScope(scope, user);
    const id = String(user.userId ?? user.oid);
    const context = scopedAccessProfile(accessContext(id), scope);
    return (context.roleMemberships ?? []).filter((assignment) => assignment.userId === id).map((assignment) => ({ ...assignment,
      role: visibleRoleResponse(find('accessRoles', assignment.roleId), scope),
      permissionCount: visibleRoleResponse(find('accessRoles', assignment.roleId), scope).permissionCount,
    }));
  });
  registerPattern('PUT', /^\/api\/Access\/users\/[^/]+\/assignments\/[^/]+$/i, (request) => {
    const scope = administrationScope(actor(), 'Administration.Assignments.Manage');
    const parts = request.path.split('/');
    const user = find('users', parts[4]);
    if (user.portal !== 'QCDOC') throw new Error('Use the vendor provider-role commands for vendor accounts.');
    assertUserWriteScope(scope, user, false);
    if (Object.keys(request.body).some((key) => key !== 'assigned') || typeof request.body.assigned !== 'boolean') throw new Error('Assignment updates accept assigned boolean only.');
    const [{ roleId }] = validateAssignmentChoices(actor(), [parts[6]]);
    const role = find('accessRoles', roleId);
    const userId = String(user.userId ?? user.oid);
    const table = db().userRoleMemberships ??= [];
    const before = table.filter((row) => row.userId === userId && row.roleId === role.oid);
    db().userRoleMemberships = table.filter((row) => row.userId !== userId || row.roleId !== role.oid);
    if (request.body.assigned) db().userRoleMemberships.push({ oid: before[0]?.oid ?? uuid(), userId, roleId: String(role.oid) });
    const target = refreshAssignmentLabels(user);
    addAudit('assignment.updated', String(user.userName), before.length > 0, request.body.assigned);
    return { userId, roleId: role.oid, assigned: request.body.assigned,
      accessProfile: scopedAccessProfile(accessProfileFor(target), administrationScope(actor(), 'Administration.Users.View')) };
  });
  registerPattern('POST', /^\/api\/Access\/check$/i, (request) => {
    const scope = administrationScope(actor(), 'Administration.Users.View');
    const user = find('users', request.body.userId);
    assertRecordScope(scope, user);
    const psl = resolvePsl(request.body.pslId);
    if (!psl) throw new Error('Choose a valid PSL.');
    if (!scope.global && !scope.pslIds.has(psl.oid)) throw new Error('This PSL is outside your administrator scope.');
    const context = accessContext(String(user.userId ?? user.oid));
    const decision = evaluateAccess(context, String(request.body.permissionKey) as AccessPermissionKey, psl.oid);
    return { ...decision, explanation: explainAccess(decision, String(psl.name)), effectivePermissions: effectivePermissions(context, psl.oid) };
  });
  registerPattern('GET', /^\/api\/Access\/audit$/i, () => {
    const scope = administrationScope(actor(), 'Administration.Audit.View');
    if (!scope.global) throw new Error('Only a Global administrator can view the access audit log.');
    return db().accessAudit ?? [];
  });
}
